import { api } from './api';

/* Types dérivés du contrat tRPC plutôt que recopiés : une colonne ajoutée côté
   serveur apparaît ici sans édition, et une colonne retirée casse à la
   compilation au lieu de mentir à l'affichage. */
type ListOutput = Awaited<ReturnType<typeof api.admin.conversations.list.query>>;
type DetailOutput = NonNullable<Awaited<ReturnType<typeof api.admin.conversations.get.query>>>;

export type AdminConversationRow = ListOutput['items'][number];
export type AdminConversationDetail = DetailOutput;
export type AdminConversationMessage = DetailOutput['messages'][number];
export type AdminConversationToolCall = AdminConversationMessage['tools'][number];
export type AdminConversationUsage = AdminConversationMessage['usage'][number];

export type AdminConversationFilter = NonNullable<
  Exclude<Parameters<typeof api.admin.conversations.list.query>[0], void>
>;

export type AdminConversationSignal = NonNullable<AdminConversationFilter['signal']>;

export async function fetchConversations(filter?: AdminConversationFilter): Promise<ListOutput> {
  return api.admin.conversations.list.query(filter);
}

export async function fetchConversation(id: string): Promise<AdminConversationDetail | null> {
  return api.admin.conversations.get.query({ id });
}

export async function fetchConversationModes(): Promise<string[]> {
  return api.admin.conversations.modes.query();
}

export type AdminConversationSummary = Awaited<ReturnType<typeof api.admin.conversations.summary.query>>;

/* La synthèse porte le MÊME filtre que la liste, moins la pagination et moins le
   signal : compter les signaux sur une population déjà filtrée par l'un d'eux
   ferait tomber tous les autres à zéro. */
export type AdminConversationScope = Omit<
  AdminConversationFilter,
  'signal' | 'commentKind' | 'sort' | 'page' | 'limit'
>;

/* Extrait PUR, et testé comme tel : c'est lui qui porte l'invariant, pas l'appel
   réseau. Le client tRPC est un Proxy, on ne le remplace pas dans un test —
   mettre l'invariant dans une fonction pure le rend éprouvable sans mensonge.

   LA REGLE : la synthèse ne se filtre jamais par ce qu'elle COMPTE. `signal` et
   `commentKind` sortent donc ; `reviewed`, `mode`, la fenêtre et la recherche
   restent, parce qu'ils décrivent la population sans être comptés. */
export function summaryScope(filter: AdminConversationFilter): AdminConversationScope {
  return {
    search: filter.search,
    ids: filter.ids,
    mode: filter.mode,
    hasVoice: filter.hasVoice,
    reviewed: filter.reviewed,
    sinceIso: filter.sinceIso,
    untilIso: filter.untilIso,
  };
}

/* LES CHIFFRES DU BANDEAU, ET LEUR SEULE SOURCE POSSIBLE.
 *
 * Cette fonction ne reçoit QUE la synthèse. Ce n'est pas une commodité : c'est
 * le garde. La carte « Conversations » lisait `total`, qui vient de la LISTE —
 * donc filtrée par signal et par nature — pendant que les quatre autres cartes
 * venaient de la synthèse, qui les exclut. Tant qu'aucune puce n'est active les
 * deux coïncident et le bandeau a l'air juste ; dès qu'on clique un signal,
 * `total` rétrécit et les autres non. DEUX PÉRIMÈTRES DANS LE MÊME BANDEAU —
 * exactement le défaut que cet écran a été repris pour supprimer, et visible
 * seulement dans l'état filtré, celui où l'on juge.
 *
 * Un test qui détecterait la troisième occurrence arriverait trop tard. Une
 * signature qui ne donne pas accès à `total` la rend inécrivable.
 *
 * LA SOUSTRACTION EST GARDÉE UNE SEULE FOIS, ICI. Elle vivait à DEUX endroits —
 * le bouton « À relire » avec un plancher à zéro, le sous-titre « sans
 * relecture » sans plancher, donc capable d'afficher un négatif. Une protection
 * écrite à un endroit et pas à l'autre diverge en silence. */
export interface AdminConversationBand {
  readonly conversations: number;
  readonly assistantTurns: number;
  readonly toolCalls: number;
  readonly toolErrors: number;
  readonly reviewed: number;
  readonly unreviewed: number;
  readonly comments: number;
  readonly costUsd: number | null;
  readonly unpricedUsage: number;
}

export function kpiBand(summary: AdminConversationSummary): AdminConversationBand {
  const reviewed = summary.threads_with_comment;
  return {
    conversations: summary.threads,
    assistantTurns: summary.assistant_turns,
    toolCalls: summary.tool_calls,
    toolErrors: summary.tool_errors,
    reviewed,
    /* Plancher à zéro : les deux nombres viennent du même périmètre, donc la
       différence ne DEVRAIT pas être négative — mais un compteur qui affiche
       « -3 conversations sans relecture » ne se corrige pas, il se remarque. */
    unreviewed: Math.max(0, summary.threads - reviewed),
    comments: Object.values(summary.by_comment_kind).reduce((a, n) => a + n, 0),
    costUsd: summary.cost_usd,
    unpricedUsage: summary.unpriced_usage_count,
  };
}

export async function fetchConversationSummary(
  filter?: AdminConversationFilter,
): Promise<AdminConversationSummary> {
  if (!filter) return api.admin.conversations.summary.query();
  return api.admin.conversations.summary.query(summaryScope(filter));
}

/* Les neuf natures de commentaire, dans l'ordre où elles se lisent en revue :
   ce que l'IA ou le relecteur a jugé faux d'abord, le positif à la fin. Source :
   docs/conversation-comments.md. */
export const COMMENT_KINDS: { id: string; label: string }[] = [
  { id: 'wrong_answer', label: 'Réponse fausse' },
  { id: 'fabricated_source', label: 'Source inventée' },
  { id: 'irrelevant_source', label: 'Source hors sujet' },
  { id: 'announced_not_done', label: 'Action annoncée, non faite' },
  { id: 'false_failure', label: 'Panne inventée' },
  { id: 'incomplete_delivery', label: 'Travail non livré' },
  { id: 'tone_or_wording', label: 'Ton ou formulation' },
  { id: 'good_example', label: 'Bon exemple' },
  { id: 'other', label: 'Autre' },
];

/* Ismael arrive avec un identifiant de document, un identifiant de fil, ou une
   URL de l'application collée telle quelle. On extrait tous les UUID de la
   saisie et on les cherche des deux côtés (fil ET document) ; le reste de la
   chaîne sert de recherche plein texte. */
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export function splitSearch(input: string): { ids: string[]; text: string } {
  const ids = [...new Set((input.match(UUID_RE) ?? []).map((v) => v.toLowerCase()))];
  const text = input.replace(UUID_RE, ' ').replace(/https?:\/\/\S+/g, ' ').trim();
  return { ids: ids.slice(0, 20), text };
}

/* La surface d'origine d'un fil. `mode` est la seule colonne fiable : elle est
   posée par la route qui sert le fil (composition/content-editor.ts pose
   `cours` avec document et `teacher` sans, chat-respond sert `general` et
   `parcours`, admin-chat pose `admin`). document_id ne distingue rien à lui
   seul — 66 fils sur 111 n'en ont pas. */
export function fmtDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  const min = Math.floor(total / 60);
  const sec = total % 60;
  return min > 0 ? `${min} min ${String(sec).padStart(2, '0')} s` : `${sec} s`;
}

export const SURFACES: Record<string, { label: string; hint: string }> = {
  cours: { label: 'Éditeur de contenu', hint: 'Chat rattaché à un document (/teacher/chat avec documentId)' },
  teacher: { label: 'Assistant enseignant', hint: 'Chat enseignant hors document (/teacher/chat sans documentId)' },
  general: { label: 'Chat élève', hint: 'Chat élève généraliste (chat-respond)' },
  parcours: { label: 'Parcours élève', hint: 'Chat élève rattaché à un parcours' },
  voice: { label: 'Voix (mobile)', hint: "Fil ouvert par le chemin vocal mobile. Le vocal du web ne cree pas de fil : il se greffe sur le fil courant — c'est le marqueur vocal (usage_records.kind='voice') qui le repere, pas ce mode." },
  admin: { label: "Labo d'outils", hint: 'Chat admin (/admin/chat)' },
};

export function surfaceOf(row: Pick<AdminConversationRow, 'mode'>): { label: string; hint: string } {
  return SURFACES[row.mode] ?? { label: row.mode, hint: `mode = ${row.mode}` };
}

export type SignalTone = 'danger' | 'warn' | 'info' | 'neutral';

export interface SignalDefinition {
  id: AdminConversationSignal;
  label: string;
  short: string;
  tone: SignalTone;
  help: string;
  count: (row: AdminConversationRow) => number;
}

export const SIGNALS: SignalDefinition[] = [
  {
    id: 'claims_blocked',
    label: "Revendique une limite ou une panne",
    short: 'limite revendiquée',
    tone: 'danger',
    help:
      "Le tour affirme avoir atteint une limite d'outils, ou invoque un problème technique. " +
      'À confronter au nombre réel d’appels du tour : les audits du 26 août ont montré des tours ' +
      'à zéro appel sur un budget intact.',
    count: (r) => r.claims_blocked_turns,
  },
  {
    id: 'announced_no_tool',
    label: 'Annonce une action sans appeler d’outil',
    short: 'annonce sans outil',
    tone: 'danger',
    help:
      'Le tour écrit « je vais créer / générer / ajouter… » et ne dispatche aucun appel. ' +
      "C'est l'écart annonce / action que rien ne détecte côté serveur.",
    count: (r) => r.announced_no_tool_turns,
  },
  {
    id: 'tool_error',
    label: "Erreur d'outil",
    short: 'erreur',
    tone: 'danger',
    help: "Un appel a renvoyé un objet contenant une clé `error` — garde-fou refusé, précondition non satisfaite, panne.",
    count: (r) => r.tool_errors,
  },
  {
    id: 'empty_result',
    label: 'Résultat vide',
    short: 'résultat vide',
    tone: 'warn',
    help:
      "Le résultat existe mais ne porte rien : objet sans clé, ou dont toutes les valeurs sont nulles, " +
      'tableaux vides, objets vides ou chaînes vides — par exemple {"items": []}.',
    count: (r) => r.empty_results,
  },
  {
    id: 'empty_text',
    label: 'Réponse sans texte',
    short: 'texte vide',
    tone: 'warn',
    help: "Le transcript persisté est vide ou vaut « (pas de texte) ».",
    count: (r) => r.empty_text_turns,
  },
  {
    id: 'at_cap',
    label: 'Tour au plafond',
    short: 'plafond',
    tone: 'info',
    help:
      "Le tour compte au moins autant d'appels que le plafond de tours de sa route. " +
      "Indice, pas preuve : plusieurs appels parallèles ne consomment qu'un seul tour.",
    count: (r) => r.turns_at_cap,
  },
  {
    id: 'zero_tool',
    label: 'Tour sans aucun outil',
    short: 'sans outil',
    tone: 'neutral',
    help: "Tour d'assistant qui n'appelle rien. Normal pour une réponse conversationnelle, suspect après une demande d'action.",
    count: (r) => r.zero_tool_turns,
  },
  {
    id: 'missing_result',
    label: 'Résultat non conservé',
    short: 'résultat non conservé',
    tone: 'neutral',
    help:
      "L'appel est antérieur à la persistance des résultats (#375, 23 août) : le résultat n'a jamais été écrit. " +
      "Ce n'est pas un résultat vide — nous ne savons simplement pas ce que l'outil a renvoyé.",
    count: (r) => r.missing_results,
  },
  {
    id: 'no_message',
    label: 'Aucun message',
    short: 'vide',
    tone: 'neutral',
    help: 'Conversation créée sans le moindre message.',
    count: (r) => (r.message_count === 0 ? 1 : 0),
  },
];

export const TONE_CLASS: Record<SignalTone, string> = {
  danger: 'bg-[var(--naia-error-bg)] text-[var(--naia-error)]',
  warn: 'bg-[var(--naia-warn-bg)] text-[var(--naia-tertiary)]',
  info: 'bg-[var(--naia-info-bg)] text-[var(--naia-blue)]',
  neutral: 'bg-[var(--naia-card-alt)] text-[var(--naia-text-secondary)]',
};

export function activeSignals(row: AdminConversationRow): Array<SignalDefinition & { value: number }> {
  return SIGNALS.map((s) => ({ ...s, value: s.count(row) })).filter((s) => s.value > 0);
}

export type ToolStatus = 'ok' | 'error' | 'empty' | 'missing';

export function toolStatus(call: AdminConversationToolCall): ToolStatus {
  if (call.is_error) return 'error';
  if (!call.has_result) return 'missing';
  if (call.is_empty) return 'empty';
  return 'ok';
}

export const TOOL_STATUS_LABEL: Record<ToolStatus, string> = {
  ok: 'résultat',
  error: 'erreur',
  empty: 'vide',
  missing: 'résultat non conservé',
};

export const TOOL_STATUS_TONE: Record<ToolStatus, SignalTone> = {
  ok: 'neutral',
  error: 'danger',
  empty: 'warn',
  missing: 'neutral',
};

export function messageSignals(message: AdminConversationMessage, roundCap: number | null): string[] {
  const out: string[] = [];
  if (message.role !== 'assistant') return out;
  if (message.claims_blocked) out.push('claims_blocked');
  if (message.announced_no_tool) out.push('announced_no_tool');
  if (message.tools.some((t) => t.is_error)) out.push('tool_error');
  if (message.tools.some((t) => t.has_result && t.is_empty)) out.push('empty_result');
  if (message.empty_text) out.push('empty_text');
  if (roundCap !== null && message.tools.length >= roundCap) out.push('at_cap');
  if (message.tools.length === 0 && !message.announced_no_tool && !message.claims_blocked) out.push('zero_tool');
  if (message.tools.some((t) => !t.has_result)) out.push('missing_result');
  return out;
}

export const SIGNAL_BY_ID = new Map(SIGNALS.map((s) => [s.id as string, s]));

/* Un résumé d'une ligne des arguments ou du résultat, pour lire un appel sans
   le déplier. On ne tronque jamais la valeur affichée dans le bloc déplié —
   seulement cet aperçu. */
export function inlinePreview(value: unknown, max = 120): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.length > max ? `${value.slice(0, max)}…` : value;
  const json = JSON.stringify(value);
  if (!json) return '—';
  return json.length > max ? `${json.slice(0, max)}…` : json;
}

export function jsonSize(value: unknown): number {
  if (value === null || value === undefined) return 0;
  return JSON.stringify(value, null, 2)?.length ?? 0;
}
